/**
 * SearchableCombobox
 *
 * A general-purpose typeahead "search-or-pick-from-list" input. Used as
 * the profession picker in both the quick SaveContactModal and the full
 * ContactForm's Service Professional section so the two places stay in
 * lockstep (both filter the same canonical option list, both accept
 * free-text for off-list values), and as the grouped position-title
 * picker on the positions form.
 *
 * Why not a plain <select> with <option>: native selects don't filter,
 * so a 30-item list like PROFESSION_OPTIONS turns into a scroll-and-
 * squint exercise. Why not <input list> + <datalist>: the cross-browser
 * styling is rough and we still want to support free-text input that
 * isn't in the option set (e.g. "Locksmith").
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';

export interface SearchableComboboxGroup {
    /** Non-selectable header rendered above the group's options. */
    label: string;
    options: readonly string[];
}

export interface SearchableComboboxProps {
    value: string;
    onChange: (next: string) => void;
    /** Options shown in the dropdown. Free-text values outside this list
     *  are still accepted via the input. Ignored when `groups` is given. */
    options?: readonly string[];
    /** Grouped options — each group renders a muted header followed by
     *  its options. Filtering spans every group; empty groups are hidden. */
    groups?: readonly SearchableComboboxGroup[];
    /** Muted text shown on the right of a row (e.g. an employment type). */
    optionHint?: (option: string) => string | undefined;
    placeholder?: string;
    disabled?: boolean;
    /** Optional className applied to the input wrapper for layout hooks. */
    className?: string;
    /** id for the text input so a visible <label htmlFor> can target it. */
    inputId?: string;
}

interface Section {
    label?: string;
    options: readonly string[];
}

const NO_OPTIONS: readonly string[] = [];

export function SearchableCombobox({
    value,
    onChange,
    options = NO_OPTIONS,
    groups,
    optionHint,
    placeholder = 'Search…',
    disabled = false,
    className,
    inputId,
}: SearchableComboboxProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    // False right after focus, true once the user types. Until they
    // type, the whole list shows (with the current choice highlighted)
    // so a pre-filled field can be changed without clearing it first.
    const [typed, setTyped] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    // Close on outside click so the dropdown doesn't hang around while
    // the user fills out other fields.
    useEffect(() => {
        if (!open) return;
        const onClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    const sections = useMemo<readonly Section[]>(
        () => (groups ? groups : [{ options }]),
        [groups, options],
    );

    const filteredSections = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!typed || !q) return sections;
        return sections
            .map((section) => ({
                ...section,
                options: section.options.filter((o) => o.toLowerCase().includes(q)),
            }))
            .filter((section) => section.options.length > 0);
    }, [typed, query, sections]);

    const hasMatches = filteredSections.some((section) => section.options.length > 0);

    // While the dropdown is open we let the input reflect what the user
    // is typing; when it's closed we surface the chosen value. This is
    // the same pattern macOS / Material comboboxes use — selection state
    // takes over once the picker is dismissed.
    const inputValue = open ? query : value;

    const pick = (option: string) => {
        onChange(option);
        setQuery('');
        setOpen(false);
    };

    return (
        <div ref={containerRef} className={`relative ${className ?? ''}`}>
            <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
                id={inputId}
                type="text"
                value={inputValue}
                disabled={disabled}
                onChange={(e) => {
                    const next = e.target.value;
                    setQuery(next);
                    setTyped(true);
                    onChange(next);
                    setOpen(true);
                }}
                onFocus={() => {
                    setQuery(value);
                    setTyped(false);
                    setOpen(true);
                }}
                placeholder={placeholder}
                className="w-full rounded-lg border border-gray-200 pl-9 pr-9 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:bg-gray-50 disabled:text-gray-400"
            />
            <button
                type="button"
                onClick={() => !disabled && setOpen((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed"
                aria-label="Toggle options"
                disabled={disabled}
            >
                <ChevronDown
                    size={16}
                    className={open ? 'rotate-180 transition-transform' : 'transition-transform'}
                />
            </button>
            {open && (
                <div className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                    {!hasMatches ? (
                        <div className="px-3 py-2 text-xs text-gray-500">
                            No match. Press Tab to keep “{query}”.
                        </div>
                    ) : (
                        filteredSections.map((section, index) => (
                            <div key={section.label ?? index} role="group" aria-label={section.label}>
                                {section.label && (
                                    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                        {section.label}
                                    </div>
                                )}
                                {section.options.map((option) => {
                                    const hint = optionHint?.(option);
                                    return (
                                        <button
                                            type="button"
                                            key={option}
                                            onClick={() => pick(option)}
                                            className={[
                                                'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-primary/5',
                                                value === option
                                                    ? 'bg-primary/5 font-medium text-primary-dark'
                                                    : 'text-gray-700',
                                            ].join(' ')}
                                        >
                                            <span className="min-w-0 truncate">{option}</span>
                                            {hint && (
                                                <span className="shrink-0 text-xs font-normal text-gray-400">{hint}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default SearchableCombobox;
